import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCurrency } from "@/hooks/use-currency";
import { Loader2, Plus, DollarSign, CheckCircle, XCircle, Clock } from "lucide-react";
import { format } from "date-fns";

interface Salesperson {
    id: string;
    name: string;
    email: string;
    total_commission_earned: number;
    total_commission_paid: number;
}

interface Payment {
    id: string;
    salesperson_id: string;
    amount: number;
    payment_date: string;
    payment_method: string | null;
    payment_status: string;
    reference_number: string | null;
    notes: string | null;
    created_at: string;
    salespeople?: Salesperson;
}

export function CommissionPayments() {
    const [salespeople, setSalespeople] = useState<Salesperson[]>([]);
    const [payments, setPayments] = useState<Payment[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { toast } = useToast();
    const { formatPrice } = useCurrency();

    const [formData, setFormData] = useState({
        salesperson_id: "",
        amount: "",
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: "Bank Transfer",
        payment_status: "PAID",
        reference_number: "",
        notes: ""
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);

        // Fetch salespeople
        const { data: peopleData } = await supabase
            .from('salespeople')
            .select('*')
            .order('name');

        setSalespeople(peopleData || []);

        // Fetch payments
        const { data: paymentsData, error } = await supabase
            .from('commission_payments')
            .select(`
        *,
        salespeople(id, name, email, total_commission_earned, total_commission_paid)
      `)
            .order('payment_date', { ascending: false });

        if (error) {
            toast({
                title: "Error fetching payments",
                description: error.message,
                variant: "destructive"
            });
        } else {
            setPayments(paymentsData || []);
        }

        setLoading(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!formData.salesperson_id || !formData.amount || !formData.payment_date) {
            toast({
                title: "Validation Error",
                description: "Salesperson, Amount, and Payment Date are required",
                variant: "destructive"
            });
            return;
        }

        const payload = {
            salesperson_id: formData.salesperson_id,
            amount: parseFloat(formData.amount),
            payment_date: formData.payment_date,
            payment_method: formData.payment_method || null,
            payment_status: formData.payment_status,
            reference_number: formData.reference_number || null,
            notes: formData.notes || null
        };

        const { error } = await supabase
            .from('commission_payments')
            .insert([payload]);

        if (error) {
            toast({
                title: "Error creating payment",
                description: error.message,
                variant: "destructive"
            });
        } else {
            // Update salesperson's total_commission_paid if status is PAID
            if (formData.payment_status === 'PAID') {
                const salesperson = salespeople.find(s => s.id === formData.salesperson_id);
                if (salesperson) {
                    await supabase
                        .from('salespeople')
                        .update({
                            total_commission_paid: salesperson.total_commission_paid + parseFloat(formData.amount)
                        })
                        .eq('id', formData.salesperson_id);
                }
            }

            toast({
                title: "Success",
                description: "Payment recorded successfully"
            });

            setIsDialogOpen(false);
            resetForm();
            fetchData();
        }
    };

    const updatePaymentStatus = async (paymentId: string, newStatus: string, oldStatus: string, amount: number, salespersonId: string) => {
        const { error } = await supabase
            .from('commission_payments')
            .update({ payment_status: newStatus })
            .eq('id', paymentId);

        if (error) {
            toast({
                title: "Error",
                description: error.message,
                variant: "destructive"
            });
            return;
        }

        // Update salesperson's total_commission_paid
        const salesperson = salespeople.find(s => s.id === salespersonId);
        if (salesperson) {
            let newTotal = salesperson.total_commission_paid;

            // If changing from PAID to something else, subtract
            if (oldStatus === 'PAID' && newStatus !== 'PAID') {
                newTotal -= amount;
            }
            // If changing to PAID from something else, add
            else if (oldStatus !== 'PAID' && newStatus === 'PAID') {
                newTotal += amount;
            }

            await supabase
                .from('salespeople')
                .update({ total_commission_paid: newTotal })
                .eq('id', salespersonId);
        }

        toast({
            title: "Status Updated",
            description: `Payment marked as ${newStatus.toLowerCase()}`
        });

        fetchData();
    };

    const resetForm = () => {
        setFormData({
            salesperson_id: "",
            amount: "",
            payment_date: new Date().toISOString().split('T')[0],
            payment_method: "Bank Transfer",
            payment_status: "PAID",
            reference_number: "",
            notes: ""
        });
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'PAID':
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case 'PENDING':
                return <Clock className="h-4 w-4 text-orange-600" />;
            case 'CANCELLED':
                return <XCircle className="h-4 w-4 text-red-600" />;
            default:
                return null;
        }
    };

    const getStatusBadge = (status: string) => {
        const variants: Record<string, "default" | "secondary" | "destructive"> = {
            'PAID': 'default',
            'PENDING': 'secondary',
            'CANCELLED': 'destructive'
        };
        return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
    };

    const totalPaid = payments
        .filter(p => p.payment_status === 'PAID')
        .reduce((sum, p) => sum + p.amount, 0);

    const totalPending = payments
        .filter(p => p.payment_status === 'PENDING')
        .reduce((sum, p) => sum + p.amount, 0);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight">Commission Payments</h2>
                    <p className="text-muted-foreground">Record and track commission payments to salespeople.</p>
                </div>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button><Plus className="mr-2 h-4 w-4" /> Record Payment</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle>Record Commission Payment</DialogTitle>
                            <DialogDescription>Enter payment details for a salesperson.</DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                            <div className="space-y-2">
                                <Label>Salesperson *</Label>
                                <Select
                                    value={formData.salesperson_id}
                                    onValueChange={(v) => {
                                        setFormData(prev => ({ ...prev, salesperson_id: v }));
                                        // Auto-fill pending amount
                                        const person = salespeople.find(s => s.id === v);
                                        if (person) {
                                            const pending = person.total_commission_earned - person.total_commission_paid;
                                            setFormData(prev => ({ ...prev, amount: pending.toString() }));
                                        }
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select salesperson" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {salespeople.map(person => {
                                            const pending = person.total_commission_earned - person.total_commission_paid;
                                            return (
                                                <SelectItem key={person.id} value={person.id}>
                                                    {person.name} ({formatPrice(pending)} pending)
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Amount *</Label>
                                <Input
                                    name="amount"
                                    type="number"
                                    step="0.01"
                                    value={formData.amount}
                                    onChange={handleInputChange}
                                    placeholder="0.00"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Payment Date *</Label>
                                <Input
                                    name="payment_date"
                                    type="date"
                                    value={formData.payment_date}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Payment Method</Label>
                                <Select
                                    value={formData.payment_method}
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, payment_method: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                                        <SelectItem value="Cash">Cash</SelectItem>
                                        <SelectItem value="PayPal">PayPal</SelectItem>
                                        <SelectItem value="Check">Check</SelectItem>
                                        <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Status</Label>
                                <Select
                                    value={formData.payment_status}
                                    onValueChange={(v) => setFormData(prev => ({ ...prev, payment_status: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="PAID">Paid</SelectItem>
                                        <SelectItem value="PENDING">Pending</SelectItem>
                                        <SelectItem value="CANCELLED">Cancelled</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Reference Number</Label>
                                <Input
                                    name="reference_number"
                                    value={formData.reference_number}
                                    onChange={handleInputChange}
                                    placeholder="e.g., TXN123456"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Notes</Label>
                                <Textarea
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Additional information..."
                                    rows={3}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                                Cancel
                            </Button>
                            <Button onClick={handleSubmit}>Record Payment</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-3">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-green-600">{formatPrice(totalPaid)}</div>
                        <p className="text-xs text-muted-foreground">
                            {payments.filter(p => p.payment_status === 'PAID').length} payments
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Pending</CardTitle>
                        <Clock className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-orange-600">{formatPrice(totalPending)}</div>
                        <p className="text-xs text-muted-foreground">
                            {payments.filter(p => p.payment_status === 'PENDING').length} payments
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Payments</CardTitle>
                        <CheckCircle className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{payments.length}</div>
                        <p className="text-xs text-muted-foreground">
                            All time
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Payments Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Payment History</CardTitle>
                    <CardDescription>All commission payments recorded in the system</CardDescription>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex justify-center p-8">
                            <Loader2 className="animate-spin h-8 w-8 text-primary" />
                        </div>
                    ) : payments.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            No payments recorded yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Salesperson</TableHead>
                                        <TableHead className="text-right">Amount</TableHead>
                                        <TableHead>Method</TableHead>
                                        <TableHead>Reference</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {payments.map(payment => (
                                        <TableRow key={payment.id}>
                                            <TableCell className="text-sm">
                                                {format(new Date(payment.payment_date), 'MMM dd, yyyy')}
                                            </TableCell>
                                            <TableCell>
                                                <div className="text-sm">
                                                    <div className="font-medium">{payment.salespeople?.name}</div>
                                                    <div className="text-muted-foreground text-xs">{payment.salespeople?.email}</div>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right font-semibold">
                                                {formatPrice(payment.amount)}
                                            </TableCell>
                                            <TableCell className="text-sm">{payment.payment_method || '-'}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {payment.reference_number || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    {getStatusIcon(payment.payment_status)}
                                                    {getStatusBadge(payment.payment_status)}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    value={payment.payment_status}
                                                    onValueChange={(newStatus) => updatePaymentStatus(
                                                        payment.id,
                                                        newStatus,
                                                        payment.payment_status,
                                                        payment.amount,
                                                        payment.salesperson_id
                                                    )}
                                                >
                                                    <SelectTrigger className="h-8 w-32">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="PAID">Paid</SelectItem>
                                                        <SelectItem value="PENDING">Pending</SelectItem>
                                                        <SelectItem value="CANCELLED">Cancelled</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
